import { createHash } from "node:crypto";
//#region src/gsa-identity.ts
/**
* Per-app-per-DP GCP service-account identity (WS-5, #1686 / #828).
*
* ⛔ **THE ONE DERIVATION IN THE PLATFORM.** `release-engine` allocates the id from
* {@link deriveAppGsaAccountId} and persists it; the `App` XR carries it as
* `spec.gsaAccountId`; the DP operator receives the resulting EMAIL on
* `AppDesiredState.gcpServiceAccount` and stamps it verbatim. ⛔ The operator MUST NOT
* re-derive — that is the rule `desired-state.ts` already states for allocated names
* ("the operator MUST consume the allocations from this sidecar, NOT synthesize them").
* A second implementation is a drift surface with no detector.
*
* ## Why the shape is what it is
*
* A GCP service-account id is 6–30 chars of `[a-z0-9-]`
* (https://docs.cloud.google.com/iam/docs/service-accounts-create, verbatim: "The ID must be
* between 6 and 30 characters, and can contain lowercase alphanumeric characters and dashes").
* ⚠️ Google's docs do NOT state a first- or last-character rule, and verifying one empirically
* would require creating a service account. So neither assumption is load-bearing here: the id
* ALWAYS begins with the `d` of `dp-` and ALWAYS ends in a base36 hash character.
* ⛔ Do not restructure this so the app slug can land at either end.
*
* The XR name is `dp-${dpName}` and `dpName` is capped at 20
* (`zion-cp-compositions/configs/xrd-dataplane.yaml`), so the app-name budget is
* `26 - dpName.length` — as little as **6 characters**. Plain truncation therefore cannot work,
* and "usually short enough" reproduces the 2026-06-23 per-org failure, where a 48-char id meant
* the ServiceAccount simply never created and nothing noticed.
*/
/** GCP service-account-id bounds. See the docblock for the citation. */
const GSA_ACCOUNT_ID_MIN_LENGTH = 6;
const GSA_ACCOUNT_ID_MAX_LENGTH = 30;
/**
* The account-id grammar this module both PRODUCES and ENFORCES.
*
* Identical to `desired-state.schemas.ts`'s local `ID_PART`, which is itself GCP's charset rule
* plus the two unverified first/last-character rules. Anchored at both ends with no unbounded
* nested quantifier, and every consumer applies the LENGTH bound before the regex (the repo's
* ReDoS rule).
*/
const GSA_ACCOUNT_ID_PATTERN = /^[a-z]([a-z0-9-]*[a-z0-9])?$/;
/**
* The account-id grammar EXPRESSED WITH ITS LENGTH BOUND INLINE, as a fragment.
*
* ⛔ Not a second grammar — the SAME one. {@link GSA_ACCOUNT_ID_PATTERN} plus the
* `[GSA_ACCOUNT_ID_MIN_LENGTH, GSA_ACCOUNT_ID_MAX_LENGTH]` bound that every consumer applies
* beside it (the repo's ReDoS rule: length first, then the regex). Over that length range the
* two forms accept exactly the same strings, and `gsa-identity.test.ts` proves it with an
* EQUIVALENCE test rather than a comment, because a comment cannot notice when one side moves.
*
* Derived from the constants so a change to either bound cannot leave this behind. The `-2`
* accounts for the mandatory first (`[a-z]`) and last (`[a-z0-9]`) characters.
*/
const ACCOUNT_ID_FRAGMENT = `[a-z][a-z0-9-]{4,28}[a-z0-9]`;
/**
* A GCP project id: 6–30 chars, same shape as an account id.
* https://docs.cloud.google.com/resource-manager/docs/creating-managing-projects — "must be 6 to
* 30 characters", lowercase letters, digits and hyphens, must start with a letter and cannot end
* with a hyphen.
*/
const PROJECT_ID_FRAGMENT = ACCOUNT_ID_FRAGMENT;
/**
* The full `<account-id>@<project>.iam.gserviceaccount.com` email, ANCHORED at both ends.
*
* ⛔ THE ANCHORS AND THE ESCAPED DOTS ARE THE SECURITY PROPERTY. `min(1)` — what
* `AppDesiredState.gcpServiceAccount` carried until 2026-08-17 — accepts any string at all, and
* an unescaped/unanchored variant accepts `…@evil.com/iam-gserviceaccount-com`. The DP operator
* stamps this value VERBATIM onto a KSA's `iam.gke.io/gcp-service-account` annotation, so the
* envelope is the last place it can be checked at all: the operator runs no zod (measured
* 2026-08-16), and GKE resolves whatever principal the annotation names.
*
* ⛔ No unbounded and no nested quantifier — every quantifier is `{n,m}` — and every consumer
* applies {@link GSA_EMAIL_MAX_LENGTH} BEFORE the match, per the repo's ReDoS rule.
*/
const GSA_SERVICE_ACCOUNT_EMAIL_PATTERN = new RegExp(`^${ACCOUNT_ID_FRAGMENT}@${PROJECT_ID_FRAGMENT}\\.iam\\.gserviceaccount\\.com$`);
/** The longest string the pattern above can match: `30 + '@' + 30 + '.iam.gserviceaccount.com'`. */
const GSA_EMAIL_MAX_LENGTH = 85;
/** The longest GCP project id: 30 characters. */
const GCP_PROJECT_ID_MAX_LENGTH = 30;
/**
* A GCP project id, anchored at both ends: 6 to 30 characters of `[a-z0-9-]`, a letter first and a
* letter or digit last. It is the project part of {@link GSA_SERVICE_ACCOUNT_EMAIL_PATTERN}.
*
* One pattern serves every place that checks a tenant data plane's project: the provisioning
* create door, the console's New data plane form, `bin/zion-dp create` and `zion-ops dp create`
* (plan 2026-10-09-zion-build-signer-identity, ruling R-012). Apply
* {@link GCP_PROJECT_ID_MAX_LENGTH} before the match. The pattern takes no trim and no case fold.
*/
const GCP_PROJECT_ID_PATTERN = new RegExp(`^${PROJECT_ID_FRAGMENT}$`);
/**
* The label key that marks a ServiceAccount, and every pod and pod template that runs as it, as
* the holder of a CP-pinned Google service account (`AppFixtureSet.pinnedGcpServiceAccount`).
*
* The zion-operator stamps it with {@link PINNED_GSA_LABEL_VALUE}. The DP chart's admission
* policies select their parameter objects by it, and refuse a token mint, a foreign pod or
* workload create and an `exec` for a labelled account (plan 2026-10-09-zion-build-signer-identity,
* rulings R-016 and R-021). The operator imports this constant. The chart's YAML and CEL carry the
* same literal, and a DP chart test compares that literal with this constant.
*/
const PINNED_GSA_LABEL_KEY = "zion.witness.ai/pinned-gsa";
/** The one value of {@link PINNED_GSA_LABEL_KEY}. */
const PINNED_GSA_LABEL_VALUE = "true";
/** Length of the base36 disambiguating hash. 6 chars = log2(36^6) = 31.02 bits. */
const GSA_HASH_LENGTH = 6;
const BASE36_SPACE = 36n ** BigInt(6);
/**
* ⛔ `|` and not `-`. Both inputs are drawn from `[a-z0-9-]`, so `|` cannot occur in either,
* which is what makes the concatenation INJECTIVE: without it, `("a-b","c")` and `("a","b-c")`
* would hash the same input string and two distinct pairs could share an identity.
*/
const HASH_SEPARATOR = "|";
/**
* 6 base36 chars of sha256(`dpName|appSlug`).
*
* 15 hex digits (60 bits) are reduced mod 36^6; the residual modulo bias is ~2e-9 and
* irrelevant at any population this platform will reach. `padStart` keeps the width FIXED at
* 6 so the id layout is invariant — a shorter hash would silently change the prefix budget.
*/
function shortHash(input) {
	const hex = createHash("sha256").update(`${input.dpName}${HASH_SEPARATOR}${input.appSlug}`).digest("hex");
	return (BigInt(`0x${hex.slice(0, 15)}`) % BASE36_SPACE).toString(36).padStart(6, "0");
}
/**
* Thrown by {@link assertValidGsaAccountId}. Carries the inputs AND the derived value so the
* message alone is diagnosable — the 2026-06-23 failure produced no message at all.
*/
var InvalidGsaAccountIdError = class extends Error {
	accountId;
	input;
	constructor(accountId, input, reason) {
		super(`invalid GCP service-account id "${accountId}" (length ${accountId.length}) derived for app "${input.appSlug}" on data plane "${input.dpName}": ${reason}. Bounds: 6-30 chars matching ${GSA_ACCOUNT_ID_PATTERN.source}`);
		this.accountId = accountId;
		this.input = input;
		this.name = "InvalidGsaAccountIdError";
	}
};
/**
* The L1 guard (WS-5 §5). {@link deriveAppGsaAccountId} cannot breach these bounds — that is
* proved by property test, not by hope — so this is a DEFENSIVE assertion whose job is to make
* a future edit to the scheme fail at the derivation site instead of producing a resource that
* quietly never creates.
*
* ⛔ Length is checked BEFORE the regex (ReDoS rule) and the two failures are reported
* separately, because "31 chars" and "starts with a digit" need different fixes.
*/
function assertValidGsaAccountId(accountId, input) {
	if (accountId.length < 6) throw new InvalidGsaAccountIdError(accountId, input, "shorter than the minimum length");
	if (accountId.length > 30) throw new InvalidGsaAccountIdError(accountId, input, "longer than the maximum length");
	if (!GSA_ACCOUNT_ID_PATTERN.test(accountId)) throw new InvalidGsaAccountIdError(accountId, input, "does not match the account-id grammar");
}
/**
* Derive the per-app-per-DP service-account id. Deterministic and total: every
* `(dpName, appSlug)` pair that the platform's own doors admit yields a valid id.
*
* ⚠️ Robust to the WIDER of the two app-slug grammars on purpose. `release.apps.slug` is
* validated by `SLUG_PATTERN` (`^[a-z0-9][a-z0-9-]*[a-z0-9]$`, up to 64 chars), which admits a
* LEADING DIGIT and CONSECUTIVE HYPHENS, while the envelope's `ID_PART` does not. Because the
* slug never sits at either end of the id, both are safe here.
*/
function deriveAppGsaAccountId(input) {
	const head = `dp-${input.dpName}`;
	const hash = shortHash(input);
	const remaining = 30 - head.length - 1 - 6;
	let accountId;
	if (remaining >= 2) {
		const prefix = input.appSlug.slice(0, remaining - 1).replace(/-+$/, "");
		accountId = prefix.length > 0 ? `${head}-${prefix}-${hash}` : `${head}-${hash}`;
	} else accountId = `${head}-${hash}`;
	assertValidGsaAccountId(accountId, input);
	return accountId;
}
/** The full GSA email. The ONLY place this string is assembled. */
function appGsaEmail(accountId, gcpProject) {
	return `${accountId}@${gcpProject}.iam.gserviceaccount.com`;
}
//#endregion
export { GCP_PROJECT_ID_MAX_LENGTH, GCP_PROJECT_ID_PATTERN, GSA_ACCOUNT_ID_MAX_LENGTH, GSA_ACCOUNT_ID_MIN_LENGTH, GSA_ACCOUNT_ID_PATTERN, GSA_EMAIL_MAX_LENGTH, GSA_HASH_LENGTH, GSA_SERVICE_ACCOUNT_EMAIL_PATTERN, InvalidGsaAccountIdError, PINNED_GSA_LABEL_KEY, PINNED_GSA_LABEL_VALUE, appGsaEmail, assertValidGsaAccountId, deriveAppGsaAccountId };

//# sourceMappingURL=gsa-identity.js.map